import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { callBackend } from '../utils/api';
import { buildPosSizingDraftRows } from '../utils/posSizingWatchlistSelection';
import {
    isValidPosSizingPublicationBase,
} from '../utils/posSizingPublicationContract';
import { POSITION_SIZING_ATTRIBUTE_CHANGESET } from '../utils/positionSizingAttributeContract';
import { parseCloudRunError } from '../components/msiDriftReviewHelpers';

function normalizeFractionValue(value) {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return null;
    return Number(parsed.toFixed(8));
}

function formatPercentStringFromFraction(value) {
    const normalized = normalizeFractionValue(value);
    if (!Number.isFinite(normalized)) return '';
    return Number((normalized * 100).toFixed(6)).toString();
}

const POS_SIZING_DRAFT_BATCH_SAVE_DEBOUNCE_MS = 150;

function rowsDiffer(left, right) {
    return JSON.stringify(left) !== JSON.stringify(right);
}

function getRowCogniveId(row) {
    if (!row || row.cognive_id == null) return '';
    return String(row.cognive_id).trim();
}

function hasExplicitFiniteNumber(value) {
    if (value == null) return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    return Number.isFinite(Number(value));
}

export function isPosSizingDraftRowComplete(row) {
    return Boolean(
        row && (
            hasExplicitFiniteNumber(row.trade_idea) ||
            hasExplicitFiniteNumber(row.pos_gmv_dollar)
        )
    );
}

function buildWatchlistStartRow(row) {
    const startRow = { cognive_id: getRowCogniveId(row) || null };
    if (hasExplicitFiniteNumber(row.trade_idea)) {
        startRow.trade_idea = Number(row.trade_idea);
    } else {
        startRow.gmv = Number(row.pos_gmv_dollar);
    }
    ['custom_idio_vol', 'custom_liquidity'].forEach((field) => {
        if (hasExplicitFiniteNumber(row[field])) {
            startRow[field] = Number(row[field]);
        }
    });
    return startRow;
}

function getIncompleteDraftBatchRowIds(rowsSnapshot, batchRowIds) {
    if (!batchRowIds?.size) return [];
    const rowsById = new Map((rowsSnapshot || []).map((row) => [row.id, row]));
    return Array.from(batchRowIds).filter((id) => {
        const row = rowsById.get(id);
        return Boolean(row && !row.toDelete && !isPosSizingDraftRowComplete(row));
    });
}

export function usePositionSizing({
    posSizingData,
    setPosSizingData,
    serverMaxPosSize,
    setServerMaxPosSize,
    serverNormalVol,
    setServerNormalVol,
    defaultTargetVolPct,
    defaultTargetVolDollar,
    defaultScaler,
    onPosSizingSaved,
    onSavingChange,
    onPendingChangesChange,
    posSizingPublicationBaseRef,
    // ({ rows, maxPosSize, normalVol }) => Promise<{ pos_sizing, added }>;
    // publishes the first draft when no draft exists yet.
    onStartFromWatchlist,
}) {
    const [originalRows, setOriginalRows] = useState([]);
    const [rows, setRows] = useState([]);
    const [deletedCount, setDeletedCount] = useState(0);
    const [addedCount, setAddedCount] = useState(0);
    // The table's sorting, as TanStack's [{ id, desc }].
    const [sorting, setSorting] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [droppedCogniveIds, setDroppedCogniveIds] = useState([]);
    const [failedTradeIdeaInferCogniveIds, setFailedTradeIdeaInferCogniveIds] = useState([]);
    const [failedTradeIdeaInferSnackOpen, setFailedTradeIdeaInferSnackOpen] = useState(false);
    const [dropSnackbarOpen, setDropSnackbarOpen] = useState(false);
    const [invalidGmvCogniveIds, setInvalidGmvCogniveIds] = useState([]);
    const [invalidGmvSnackbarOpen, setInvalidGmvSnackbarOpen] = useState(false);
    const [warningDialogOpen, setWarningDialogOpen] = useState(false);
    const [warningSummary, setWarningSummary] = useState(null);
    const [draftBatchRowIds, setDraftBatchRowIds] = useState(() => new Set());
    const rowsRef = useRef([]);
    const originalRowsRef = useRef([]);
    const localMaxPosSizeRef = useRef(serverMaxPosSize);
    const localNormalVolRef = useRef(serverNormalVol);
    const savingRef = useRef(false);
    const queuedSaveRef = useRef(null);
    const persistChangesRef = useRef(null);
    const draftBatchRowIdsRef = useRef(new Set());
    const batchAutoSaveTimerRef = useRef(null);
    const batchAutoSaveOptionsRef = useRef(null);

    const [localMaxPosSize, setLocalMaxPosSize] = useState(serverMaxPosSize);
    const [localMaxPosSizeStr, setLocalMaxPosSizeStr] = useState(
      serverMaxPosSize != null ? formatPercentStringFromFraction(serverMaxPosSize) : ""
    );
    const [localNormalVol, setLocalNormalVol] = useState(serverNormalVol);
    const [localNormalVolStr, setLocalNormalVolStr] = useState(
      serverNormalVol != null ? formatPercentStringFromFraction(serverNormalVol) : ""
    );

    const setRowsAndRef = useCallback((nextRows) => {
        rowsRef.current = nextRows;
        setRows(nextRows);
    }, []);

    const updateRows = useCallback((updater) => {
        setRows(prev => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            rowsRef.current = next;
            return next;
        });
    }, []);

    const setOriginalRowsAndRef = useCallback((nextRows) => {
        originalRowsRef.current = nextRows;
        setOriginalRows(nextRows);
    }, []);

    const setDraftBatchRowIdsAndRef = useCallback((nextIds) => {
        const normalized = new Set(nextIds || []);
        draftBatchRowIdsRef.current = normalized;
        setDraftBatchRowIds(normalized);
    }, []);

    const clearBatchAutoSave = useCallback(() => {
        if (batchAutoSaveTimerRef.current != null) {
            clearTimeout(batchAutoSaveTimerRef.current);
            batchAutoSaveTimerRef.current = null;
        }
        batchAutoSaveOptionsRef.current = null;
    }, []);

    useEffect(() => {
      setLocalMaxPosSize(serverMaxPosSize);
      setLocalMaxPosSizeStr(serverMaxPosSize != null ? formatPercentStringFromFraction(serverMaxPosSize) : "");
    }, [serverMaxPosSize]);

    useEffect(() => {
      setLocalNormalVol(serverNormalVol);
      setLocalNormalVolStr(serverNormalVol != null ? formatPercentStringFromFraction(serverNormalVol) : "");
    }, [serverNormalVol]);

    useEffect(() => {
        rowsRef.current = rows;
    }, [rows]);

    useEffect(() => {
        originalRowsRef.current = originalRows;
    }, [originalRows]);

    useEffect(() => {
        localMaxPosSizeRef.current = localMaxPosSize;
    }, [localMaxPosSize]);

    useEffect(() => {
        localNormalVolRef.current = localNormalVol;
    }, [localNormalVol]);

    useEffect(() => {
        savingRef.current = saving;
    }, [saving]);

    useEffect(() => () => {
        onSavingChange?.(false);
    }, [onSavingChange]);

    useEffect(() => () => {
        clearBatchAutoSave();
        queuedSaveRef.current = null;
        persistChangesRef.current = null;
    }, [clearBatchAutoSave]);

    const maxPosSizeIsDirty = normalizeFractionValue(localMaxPosSize) !== normalizeFractionValue(serverMaxPosSize);
    const normalVolIsDirty = normalizeFractionValue(localNormalVol) !== normalizeFractionValue(serverNormalVol);


    const buildDirtyRows = useCallback((rowsSnapshot, originalSnapshot) => (
        (rowsSnapshot || []).filter(
            r =>
                r.isNewRow ||
                rowsDiffer(
                    r,
                    (originalSnapshot || []).find(o => o.id === r.id)
                )
        )
    ), []);

    const mergeQueuedSave = useCallback((current = null, next = {}) => ({
        rowsOverride: next.rowsOverride ?? current?.rowsOverride,
        maxPosSizeOverride: next.maxPosSizeOverride ?? current?.maxPosSizeOverride,
        normalVolOverride: next.normalVolOverride ?? current?.normalVolOverride,
        forcePersist: Boolean(current?.forcePersist || next.forcePersist),
    }), []);

    const isTradeEditable = params => {
        if (params.row.isNewRow) return params.row.pos_gmv_dollar == null;
        const orig = originalRows.find(o => o.id === params.id)?.pos_gmv_dollar;
        const curr = rows.find(r => r.id === params.id)?.pos_gmv_dollar;
        return orig === curr;
    };
    const isGmvEditable = params => {
        if (params.row.isNewRow) return params.row.trade_idea == null;
        const orig = originalRows.find(o => o.id === params.id)?.trade_idea;
        const curr = rows.find(r => r.id === params.id)?.trade_idea;
        return orig === curr;
    };

    const dirtyRows = useMemo(
        () => buildDirtyRows(rows, originalRows),
        [rows, originalRows, buildDirtyRows]
    );

    const incompleteDraftBatchRowIds = useMemo(
        () => getIncompleteDraftBatchRowIds(rows, draftBatchRowIds),
        [rows, draftBatchRowIds]
    );

    const hasPendingChanges =
        saving ||
        dirtyRows.length > 0 ||
        maxPosSizeIsDirty ||
        normalVolIsDirty;

    useEffect(() => {
        onPendingChangesChange?.(hasPendingChanges);
    }, [hasPendingChanges, onPendingChangesChange]);

    useEffect(() => () => {
        onPendingChangesChange?.(false);
    }, [onPendingChangesChange]);

    const normalizePosSizingRow = useCallback((row, idx) => ({
        ...row,
        // Pos-sizing membership is canonical-identity native. A local row key
        // is only a defensive rendering fallback, never an identity resolver.
        id: row.cognive_id ?? row.id ?? `row_${idx}`,
        toDelete: false,
        custom_liquidity: row.custom_liquidity ?? null,
        custom_idio_vol: row.idio_vol_annual_custom ?? null,
    }), []);

     useEffect(() => {
        if (saving || dirtyRows.length) return;
        const withIds = (posSizingData || []).map(normalizePosSizingRow);
        setOriginalRowsAndRef(withIds);
        setRowsAndRef(withIds);
        setError('');
    }, [posSizingData, saving, dirtyRows.length, normalizePosSizingRow, setOriginalRowsAndRef, setRowsAndRef]);

    const handleMaxPosSizeClear = useCallback(() => {
        setLocalMaxPosSize(serverMaxPosSize);
        setLocalMaxPosSizeStr(
            serverMaxPosSize != null ? formatPercentStringFromFraction(serverMaxPosSize) : ""
        );
        }, [serverMaxPosSize]);

    const handleNormalVolClear = useCallback(() => {
      setLocalNormalVol(serverNormalVol);
      setLocalNormalVolStr(
        serverNormalVol != null ? formatPercentStringFromFraction(serverNormalVol) : ""
      );
    }, [serverNormalVol]);

    // While unsaved new rows exist the table shows them unsorted at the top;
    // a sort chosen meanwhile is kept for later only on a field of the rows.
    const handleSortingChange = useCallback((nextSorting) => {
        if (rows.some(r => r.isNewRow)) {
            const firstSavedRow = rows.find(r => !r.isNewRow);
            setSorting(nextSorting.filter(sort => firstSavedRow && sort.id in firstSavedRow));
        } else {
            setSorting(nextSorting);
        }
    }, [rows]);

    const handleUndoCell = useCallback((id, field) => {
        updateRows(rs =>
            rs.map(r =>
                r.id === id
                    ? { ...r, [field]: originalRows.find(o => o.id === id)[field] }
                    : r
            )
        );
    }, [originalRows, updateRows]);

    const handleAddWatchlistRows = useCallback((watchlistRows) => {
        const currentRows = rowsRef.current;
        const drafts = buildPosSizingDraftRows(watchlistRows, currentRows);
        if (!drafts.length) return;

        clearBatchAutoSave();
        setRowsAndRef([...drafts, ...currentRows]);
        setDraftBatchRowIdsAndRef(new Set([
            ...draftBatchRowIdsRef.current,
            ...drafts.map((row) => row.id),
        ]));
    }, [clearBatchAutoSave, setDraftBatchRowIdsAndRef, setRowsAndRef]);

    const persistChanges = useCallback(async ({
        rowsOverride,
        maxPosSizeOverride,
        normalVolOverride,
        continueSaveChain = false,
        forcePersist = false,
    } = {}) => {
        const rowsSnapshot = Array.isArray(rowsOverride) ? rowsOverride : rowsRef.current;
        const persistedDraftBatchIds = new Set(draftBatchRowIdsRef.current);
        const originalSnapshot = originalRowsRef.current;
        const maxPosSizeValue =
            normalizeFractionValue(maxPosSizeOverride != null ? maxPosSizeOverride : localMaxPosSizeRef.current);
        const normalVolValue =
            normalizeFractionValue(normalVolOverride != null ? normalVolOverride : localNormalVolRef.current);
        const maxDirty = maxPosSizeValue !== normalizeFractionValue(serverMaxPosSize);
        const normalDirty = normalVolValue !== normalizeFractionValue(serverNormalVol);
        const dirtyRowsSnapshot = buildDirtyRows(rowsSnapshot, originalSnapshot);
        const shouldPersist = forcePersist || dirtyRowsSnapshot.length > 0 || maxDirty || normalDirty;

        if (!shouldPersist) {
            return false;
        }

        if (savingRef.current && !continueSaveChain) {
            queuedSaveRef.current = mergeQueuedSave(queuedSaveRef.current, {
                rowsOverride: rowsSnapshot,
                maxPosSizeOverride: maxPosSizeValue,
                normalVolOverride: normalVolValue,
                forcePersist,
            });
            return false;
        }

        if (!continueSaveChain) {
            savingRef.current = true;
            setSaving(true);
            onSavingChange?.(true);
        }
        let startingFromWatchlist = false;
        try {
            const publicationBase = posSizingPublicationBaseRef.current;
            if (!isValidPosSizingPublicationBase(publicationBase)) {
                throw new Error('Position Sizing changed. Refresh before saving.');
            }
            if (publicationBase.portfolio_id === null) {
                // No draft exists yet, so only new rows can be pending. The
                // first save creates the draft from them; settings alone wait.
                startingFromWatchlist = true;
                const startRows = dirtyRowsSnapshot
                    .filter(r => r.isNewRow && !r.toDelete)
                    .map(buildWatchlistStartRow);
                if (startRows.length === 0) {
                    setServerMaxPosSize(maxPosSizeValue);
                    setServerNormalVol(normalVolValue);
                    return true;
                }
                if (typeof onStartFromWatchlist !== 'function') {
                    throw new Error('Position Sizing changed. Refresh before saving.');
                }
                const started = await onStartFromWatchlist({
                    rows: startRows,
                    maxPosSize: maxPosSizeValue,
                    normalVol: normalVolValue,
                });
                setServerMaxPosSize(maxPosSizeValue);
                setServerNormalVol(normalVolValue);
                setAddedCount(Number.isFinite(started?.added) ? started.added : startRows.length);
                const fresh = (started?.pos_sizing || []).map(normalizePosSizingRow);
                setRowsAndRef(fresh);
                setOriginalRowsAndRef(fresh);
                if (persistedDraftBatchIds.size) {
                    setDraftBatchRowIdsAndRef(
                        new Set(
                            Array.from(draftBatchRowIdsRef.current)
                                .filter((id) => !persistedDraftBatchIds.has(id))
                        )
                    );
                }
                setPosSizingData?.(started?.pos_sizing || []);
                setWarningSummary(null);
                setWarningDialogOpen(false);
                setError('');
                return true;
            }
            const prevOrder = rowsSnapshot.map(r => r.id);

            const toDeleteCogniveIds = rowsSnapshot
                .filter(r => r.toDelete)
                .map(r => getRowCogniveId(r))
                .filter(Boolean);

            const newBatch = dirtyRowsSnapshot
                .filter(r => r.isNewRow)
                .map(r => {
                    const tradeIdea = hasExplicitFiniteNumber(r.trade_idea)
                        ? { trade_idea: r.trade_idea }
                        : {};
                    return {
                        cognive_id: getRowCogniveId(r) || null,
                        ticker: r.ticker ? String(r.ticker).trim() : null,
                        ...tradeIdea,
                        custom_liquidity: r.custom_liquidity ?? null,
                        custom_idio_vol: r.custom_idio_vol ?? null,
                    };
                });

            const gmvEdited = dirtyRowsSnapshot.filter(r =>
                r.pos_gmv_dollar != null &&
                (r.isNewRow ||
                    originalSnapshot.find(o => o.id === r.id)?.pos_gmv_dollar !== r.pos_gmv_dollar)
            );

            const gmvPayload = gmvEdited.map(r => ({
                cognive_id: getRowCogniveId(r) || null,
                ticker: r.ticker ? String(r.ticker).trim() : null,
                gmv: r.pos_gmv_dollar,
                idio_vol_custom: r.custom_idio_vol ?? null,
                liq_factor_custom: r.custom_liquidity ?? null,
            }));

            const ideaUpdates = rowsSnapshot
                .filter(r =>
                    !r.toDelete &&
                    !r.isNewRow &&
                    rowsDiffer(
                        r,
                        originalSnapshot.find(o => o.id === r.id)
                    )
                )
                .map(r => ({
                    cognive_id: getRowCogniveId(r) || null,
                    ticker: r.ticker ?? null,
                    trade_idea: r.trade_idea,
                    custom_liquidity: r.custom_liquidity ?? null,
                    custom_idio_vol: r.custom_idio_vol ?? null,
                }));

            const saveResp = await callBackend(
                'save_pos_sizing_attribute_changes_v2',
                'POST',
                {
                    contract: POSITION_SIZING_ATTRIBUTE_CHANGESET,
                    publication_base: publicationBase,
                    deleted_cognive_ids: toDeleteCogniveIds,
                    new_rows: newBatch,
                    updated_rows: ideaUpdates,
                    gmv_edits: gmvPayload,
                    max_pos_size: maxPosSizeValue,
                    normal_vol: normalVolValue,
                    default_target_vol_pct: defaultTargetVolPct,
                    default_target_vol_dollar: defaultTargetVolDollar,
                    default_scaler: defaultScaler,
                }
            );

            const counts = saveResp?.counts || {};
            const warnings = saveResp?.warnings || {};
            if (!isValidPosSizingPublicationBase(saveResp?.publication_base)) {
                throw new Error('Position sizing save did not return a current publication base.');
            }
            posSizingPublicationBaseRef.current = saveResp.publication_base;
            setServerMaxPosSize(maxPosSizeValue);
            setServerNormalVol(normalVolValue);

            setDeletedCount(Number.isFinite(counts.deleted) ? counts.deleted : toDeleteCogniveIds.length);
            setAddedCount(Number.isFinite(counts.added) ? counts.added : newBatch.length);
            const fresh = (saveResp?.pos_sizing || []).map(normalizePosSizingRow);
            const newRows = fresh.filter(r => !prevOrder.includes(r.id));
            const existingRows = fresh
                .filter(r => prevOrder.includes(r.id))
                .sort((a, b) => prevOrder.indexOf(a.id) - prevOrder.indexOf(b.id));
            const final = [...newRows, ...existingRows];

            setRowsAndRef(final);
            setOriginalRowsAndRef(final);
            if (persistedDraftBatchIds.size) {
                setDraftBatchRowIdsAndRef(
                    new Set(
                        Array.from(draftBatchRowIdsRef.current)
                            .filter((id) => !persistedDraftBatchIds.has(id))
                    )
                );
            }
            setPosSizingData?.(saveResp?.pos_sizing || []);

            const warningLists = {
                ignored_watchlist_cognive_ids: warnings.ignored_watchlist_cognive_ids || [],
                dropped_watchlist_cognive_ids: warnings.dropped_watchlist_cognive_ids || [],
                invalid_gmv_cognive_ids: warnings.invalid_gmv_cognive_ids || [],
                dropped_cognive_ids_from_calc: warnings.dropped_cognive_ids_from_calc || [],
                failed_trade_idea_infer_cognive_ids: warnings.failed_trade_idea_infer_cognive_ids || [],
            };
            const hasWarnings = Object.values(warningLists).some(list => Array.isArray(list) && list.length > 0);
            if (hasWarnings) {
                setWarningSummary({
                    ...warningLists,
                    ignored_watchlist_cognive_ids:
                        warnings.ignored_watchlist_identifiers
                        || warningLists.ignored_watchlist_cognive_ids,
                });
                setWarningDialogOpen(true);
            } else {
                setWarningSummary(null);
                setWarningDialogOpen(false);
            }

            setInvalidGmvCogniveIds(warningLists.invalid_gmv_cognive_ids || []);
            setFailedTradeIdeaInferCogniveIds(warningLists.failed_trade_idea_infer_cognive_ids || []);
            setDroppedCogniveIds(warningLists.dropped_cognive_ids_from_calc || []);
            onPosSizingSaved?.({
                waitForAuthoritativeStatus: false,
                attributeWorkspace: {
                    state: saveResp?.attribute_state,
                    calculation: saveResp?.attribute_calculation,
                    publicationBase: saveResp?.publication_base,
                },
            });
            setError('');
            return true;
        } catch (e) {
            // A failed first save re-reads the workspace, which restores the
            // no-draft base when starting from the Watchlist is still allowed.
            if (posSizingPublicationBaseRef && !startingFromWatchlist) {
                posSizingPublicationBaseRef.current = null;
            }
            console.error(e);
            setError(parseCloudRunError(e).message);
            return false;
        } finally {
            const queuedSave = queuedSaveRef.current;
            queuedSaveRef.current = null;
            if (queuedSave) {
                void persistChangesRef.current?.({
                    ...queuedSave,
                    continueSaveChain: true,
                });
                return;
            }
            savingRef.current = false;
            setSaving(false);
            onSavingChange?.(false);
        }
    }, [
        serverMaxPosSize,
        serverNormalVol,
        defaultTargetVolPct,
        defaultTargetVolDollar,
        defaultScaler,
        buildDirtyRows,
        clearBatchAutoSave,
        mergeQueuedSave,
        normalizePosSizingRow,
        setOriginalRowsAndRef,
        setRowsAndRef,
        onPosSizingSaved,
        onSavingChange,
        onStartFromWatchlist,
        posSizingPublicationBaseRef,
        setPosSizingData,
        setServerMaxPosSize,
        setServerNormalVol,
        setDraftBatchRowIdsAndRef,
    ]);
    persistChangesRef.current = persistChanges;

    const requestAutoSave = useCallback((options = {}) => {
        const rowsSnapshot = Array.isArray(options.rowsOverride)
            ? options.rowsOverride
            : rowsRef.current;
        const activeDraftBatchIds = draftBatchRowIdsRef.current;

        if (getIncompleteDraftBatchRowIds(rowsSnapshot, activeDraftBatchIds).length > 0) {
            clearBatchAutoSave();
            return false;
        }

        if (activeDraftBatchIds.size > 0) {
            batchAutoSaveOptionsRef.current = mergeQueuedSave(
                batchAutoSaveOptionsRef.current,
                { ...options, rowsOverride: rowsSnapshot }
            );
            if (batchAutoSaveTimerRef.current != null) {
                clearTimeout(batchAutoSaveTimerRef.current);
            }
            batchAutoSaveTimerRef.current = setTimeout(() => {
                batchAutoSaveTimerRef.current = null;
                const latestRows = rowsRef.current;
                if (
                    getIncompleteDraftBatchRowIds(
                        latestRows,
                        draftBatchRowIdsRef.current
                    ).length > 0
                ) {
                    batchAutoSaveOptionsRef.current = null;
                    return;
                }
                const pendingOptions = batchAutoSaveOptionsRef.current || {};
                batchAutoSaveOptionsRef.current = null;
                void persistChangesRef.current?.({
                    ...pendingOptions,
                    rowsOverride: latestRows,
                });
            }, POS_SIZING_DRAFT_BATCH_SAVE_DEBOUNCE_MS);
            return true;
        }

        void persistChangesRef.current?.(options);
        return true;
    }, [clearBatchAutoSave, mergeQueuedSave]);

    const handleRemoveNewRow = useCallback(id => {
        const nextRows = rowsRef.current.filter(r => r.id !== id);
        const nextBatchIds = new Set(draftBatchRowIdsRef.current);
        nextBatchIds.delete(id);
        clearBatchAutoSave();
        setRowsAndRef(nextRows);
        setDraftBatchRowIdsAndRef(nextBatchIds);
        requestAutoSave({ rowsOverride: nextRows });
    }, [
        clearBatchAutoSave,
        requestAutoSave,
        setDraftBatchRowIdsAndRef,
        setRowsAndRef,
    ]);

    const processRowUpdate = useCallback((updated, original) => {
        const nextRows = rowsRef.current.map(r => (r.id === updated.id ? updated : r));
        setRowsAndRef(nextRows);
        if (rowsDiffer(updated, original)) {
            requestAutoSave({ rowsOverride: nextRows });
        }
        return updated;
    }, [requestAutoSave, setRowsAndRef]);

    const handleMaxPosSizeCommit = useCallback((nextValue) => {
        const normalizedNextValue = normalizeFractionValue(nextValue);
        if (normalizedNextValue == null) return;
        setLocalMaxPosSize(normalizedNextValue);
        setLocalMaxPosSizeStr(formatPercentStringFromFraction(normalizedNextValue));
        localMaxPosSizeRef.current = normalizedNextValue;
        requestAutoSave({ maxPosSizeOverride: normalizedNextValue });
    }, [requestAutoSave]);

    const handleNormalVolCommit = useCallback((nextValue) => {
        const normalizedNextValue = normalizeFractionValue(nextValue);
        if (normalizedNextValue == null) return;
        setLocalNormalVol(normalizedNextValue);
        setLocalNormalVolStr(formatPercentStringFromFraction(normalizedNextValue));
        localNormalVolRef.current = normalizedNextValue;
        requestAutoSave({ normalVolOverride: normalizedNextValue });
    }, [requestAutoSave]);

    // The Sizing defaults dialog applies both settings as the toolbar inputs
    // do (trade ideas held, GMVs follow), in one save it can wait for.
    const commitSizingSettings = useCallback(async ({ maxPosSize, normalVol }) => {
        const nextMaxPosSize = normalizeFractionValue(maxPosSize);
        const nextNormalVol = normalizeFractionValue(normalVol);
        if (
            nextMaxPosSize == null
            || nextNormalVol == null
            || savingRef.current
            || getIncompleteDraftBatchRowIds(rowsRef.current, draftBatchRowIdsRef.current).length > 0
        ) {
            return false;
        }
        setLocalMaxPosSize(nextMaxPosSize);
        setLocalMaxPosSizeStr(formatPercentStringFromFraction(nextMaxPosSize));
        localMaxPosSizeRef.current = nextMaxPosSize;
        setLocalNormalVol(nextNormalVol);
        setLocalNormalVolStr(formatPercentStringFromFraction(nextNormalVol));
        localNormalVolRef.current = nextNormalVol;
        clearBatchAutoSave();
        return Boolean(await persistChangesRef.current?.({
            maxPosSizeOverride: nextMaxPosSize,
            normalVolOverride: nextNormalVol,
        }));
    }, [clearBatchAutoSave]);

    const handleToggleDelete = useCallback((id) => {
        const nextRows = rowsRef.current.map(r =>
            r.id === id && !r.isNewRow ? { ...r, toDelete: !r.toDelete } : r
        );
        setRowsAndRef(nextRows);
        if (nextRows.find(r => r.id === id)?.toDelete) {
            requestAutoSave({ rowsOverride: nextRows });
        }
    }, [requestAutoSave, setRowsAndRef]);



    return {
        data: {
            rows, 
            originalRows, 
            processRowUpdate,
        },
        ui: {
            sorting, 
            error, 
            droppedCogniveIds,
            invalidGmvCogniveIds,
            warningSummary,
            warningDialogOpen,
            saving,
            isGmvEditable, 
            isTradeEditable,
            localMaxPosSize,
            setLocalMaxPosSize,
            localMaxPosSizeStr,
            setLocalMaxPosSizeStr,
            localNormalVol,
            setLocalNormalVol,
            localNormalVolStr,
            setLocalNormalVolStr,
            maxPosSizeIsDirty,
            normalVolIsDirty,
            incompleteDraftBatchRowIds,
            outstandingNewDraftCount: incompleteDraftBatchRowIds.length,
            isIncompleteNewDraftRow: id => incompleteDraftBatchRowIds.includes(id),
            isCompletedNewDraftRow: id => (
                draftBatchRowIds.has(id)
                && !incompleteDraftBatchRowIds.includes(id)
            ),
            handleMaxPosSizeClear,
            handleNormalVolClear,
            setError, 
            setWarningDialogOpen,
        },
        snack: {
            deletedCount,
            addedCount,
            dropSnackbarOpen,
            invalidGmvSnackbarOpen,
            failedTradeIdeaInferSnackOpen,
            failedTradeIdeaInferCogniveIds,
            setDeletedCount,
            setAddedCount,
            setDropSnackbarOpen,
            setInvalidGmvSnackbarOpen,
            setFailedTradeIdeaInferSnackOpen,
        },
        handlers: { 
            handleAddWatchlistRows,
            handleSortingChange,
            handleUndoCell,
            handleRemoveNewRow,
            handleToggleDelete,
            handleMaxPosSizeCommit,
            handleNormalVolCommit,
            commitSizingSettings,
        },
    };
}
